import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authApi, session } from '../services/api'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  // True when the logged-in account still has a temp password and must reset
  // it before using the app (provisioned client admins / invited users).
  const [mustChangePassword, setMustChangePassword] = useState(false)

  // Restore the session on mount: the access token lives only in memory, so
  // a reload starts unauthenticated and silently re-authenticates via the
  // HttpOnly refresh cookie (no credential ever read from storage).
  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      try {
        const response = await authApi.refresh()
        if (!cancelled && response?.data?.access_token) {
          session.setAccessToken(response.data.access_token)
          await fetchUser()
          return
        }
      } catch {
        // No valid refresh cookie -> anonymous; fall through
      }
      if (!cancelled) {
        setLoading(false)
      }
    }
    restore()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchUser = async () => {
    try {
      const response = await authApi.me()
      setUser(response.data)
      setIsAuthenticated(true)
    } catch (error) {
      // Token is invalid or expired
      logger.error('Failed to fetch user:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (username, password) => {
    try {
      const response = await authApi.login({ username, password })
      const { access_token, must_change_password } = response.data

      // Access token in MEMORY only. The refresh token is already stored by
      // the server as an HttpOnly cookie — nothing touches localStorage.
      session.setAccessToken(access_token)

      // Forced password reset for temp-password accounts.
      setMustChangePassword(Boolean(must_change_password))

      // Fetch user info
      await fetchUser()

      toast.success('Login successful!')
      return true
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed'
      toast.error(message)
      return false
    }
  }

  const logout = useCallback(async () => {
    // Revoke tokens on the server (best-effort), then clear local state
    try {
      await authApi.logout()
    } catch (e) {
      // Server call failed — still clear local state
    }

    // Clear user state
    setUser(null)
    setIsAuthenticated(false)
    setMustChangePassword(false)

    toast.success('Logged out successfully')
  }, [])

  const hasRole = useCallback(
    (role) => {
      if (!user) return false
      return user.role === role
    },
    [user]
  )

  const hasAnyRole = useCallback(
    (...roles) => {
      if (!user) return false
      return roles.includes(user.role)
    },
    [user]
  )

  const value = {
    user,
    loading,
    isAuthenticated,
    mustChangePassword,
    setMustChangePassword,
    login,
    logout,
    hasRole,
    hasAnyRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
